import { buildingApi, employeesApi, roomApi } from 'api';
import { format } from 'date-fns';
import * as ExcelJS from 'exceljs';
import { t } from 'i18next';
import {
    addLogoImageToReport,
    downloadReport,
    reportAddTitleRow,
    reportCreateWoksheet,
    reportCreateWorkbook,
} from 'utils/utils';

/**
 * Housing report — now based on EmployeeRoomAssignment (the same data the
 * room-assignment tableau renders) instead of the legacy EmployeeToSeason.roomId
 * direct relation. Includes per-assignment start/end dates and cross-season
 * employees (annual / unlimited contracts that span the current season).
 */

const HEADER_KEYS = [
    'housing.report.building',
    'housing.report.room',
    'housing.report.bed',
    'housing.report.firstName',
    'housing.report.lastName',
    'housing.report.startDate',
    'housing.report.endDate',
    'housing.report.season',
    'housing.report.departments',
] as const;

const COLUMNS_NUMBER = HEADER_KEYS.length;
const CROSS_SEASON_FONT_COLOR = 'FFB45309'; // amber-700 — visually distinct
const NORMAL_FONT_COLOR = 'FF111827';

const formatDate = (d: string | Date | null | undefined) =>
    d ? format(new Date(d), 'dd/MM/yyyy') : '';

const addHeaderRow = (worksheet: ExcelJS.Worksheet) => {
    const row = worksheet.insertRow(2, HEADER_KEYS.map((k) => t(k)));
    row.font = { name: 'Arial', family: 2, size: 12, bold: true };
};

interface AssignmentRow {
    bedIndex: number;
    firstName: string;
    lastName: string;
    startDate: string;
    endDate: string;
    seasonName: string;
    seasonId: number;
    departments: string;
    isCrossSeason: boolean;
}

interface RoomGroup {
    name: string;
    assignments: AssignmentRow[];
}

interface BuildingGroup {
    name: string;
    rooms: RoomGroup[];
}

const writeBody = (
    worksheet: ExcelJS.Worksheet,
    buildings: BuildingGroup[],
) => {
    const border = {
        top: { style: 'thin' as const, color: { argb: 'FF000000' } },
        bottom: { style: 'thin' as const, color: { argb: 'FF000000' } },
    };

    let row = 3;
    for (const building of buildings) {
        const buildingStartRow = row;
        const buildingCell = worksheet.getCell(row, 1);
        buildingCell.value = building.name;

        if (building.rooms.length === 0) {
            const tempCell = worksheet.getCell(row, 2);
            tempCell.value = t('housing.report.emptyBuilding');
            worksheet.getCell(row, COLUMNS_NUMBER).merge(tempCell);
            for (let i = 1; i <= COLUMNS_NUMBER; i++) worksheet.getCell(row, i).border = border;
            row += 1;
            continue;
        }

        for (const room of building.rooms) {
            const roomStartRow = row;
            const roomCell = worksheet.getCell(row, 2);
            roomCell.value = room.name;

            if (room.assignments.length === 0) {
                const tempCell = worksheet.getCell(row, 3);
                tempCell.value = t('housing.report.emptyRoom');
                worksheet.getCell(row, COLUMNS_NUMBER).merge(tempCell);
                for (let i = 2; i <= COLUMNS_NUMBER; i++) worksheet.getCell(row, i).border = border;
                row += 1;
                continue;
            }

            for (const a of room.assignments) {
                worksheet.getCell(row, 3).value = a.bedIndex + 1;
                worksheet.getCell(row, 4).value = a.firstName;
                worksheet.getCell(row, 5).value = a.lastName;
                worksheet.getCell(row, 6).value = a.startDate;
                worksheet.getCell(row, 7).value = a.endDate;
                worksheet.getCell(row, 8).value = a.seasonName;
                worksheet.getCell(row, 9).value = a.departments;

                for (let i = 3; i <= COLUMNS_NUMBER; i++) {
                    const cell = worksheet.getCell(row, i);
                    cell.border = border;
                    cell.font = {
                        name: 'Arial',
                        family: 2,
                        size: 11,
                        color: { argb: a.isCrossSeason ? CROSS_SEASON_FONT_COLOR : NORMAL_FONT_COLOR },
                        italic: a.isCrossSeason,
                    };
                }
                row += 1;
            }

            // Merge room name across its rows
            if (row - 1 > roomStartRow) {
                worksheet.getCell(row - 1, 2).merge(roomCell);
            }
        }

        // Merge building name across its rows
        if (row - 1 > buildingStartRow) {
            worksheet.getCell(row - 1, 1).merge(buildingCell);
        }
    }
};

const styleWorksheet = (
    worksheet: ExcelJS.Worksheet,
    rowsNumber: number,
) => {
    for (let i = 1; i <= rowsNumber; i++) {
        for (let j = 1; j <= COLUMNS_NUMBER; j++) {
            const cell = worksheet.getCell(i, j);
            cell.alignment = { vertical: 'middle', horizontal: 'center' };
            if (i <= 2) continue; // title + header keep default fill
            cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: i % 2 === 0 ? 'd6eaf8' : 'aed6f1' },
            };
        }
    }
};

const buildBuildingGroups = (
    buildingsApiData: Array<{ id: number; name: string }>,
    roomsByBuilding: Map<number, Array<{ id: number; name: string }>>,
    assignments: any[],
    currentSeasonId: number,
    departmentsByEmployee: Map<number, Map<number, string[]>>,
): BuildingGroup[] => {
    return buildingsApiData.map((b) => {
        const rooms = (roomsByBuilding.get(b.id) ?? []).map((r) => {
            const roomAssignments: AssignmentRow[] = assignments
                .filter((a: any) => a.roomId === r.id)
                .map((a: any): AssignmentRow => {
                    const employee = a.employee ?? {};
                    const season = a.season ?? {};
                    const isCrossSeason = a.seasonId !== currentSeasonId;
                    const deptsForSeason = departmentsByEmployee.get(a.employeeId)?.get(a.seasonId) ?? [];
                    return {
                        bedIndex: a.bedIndex ?? 0,
                        firstName: employee.firstName ?? '',
                        lastName: employee.lastName ?? '',
                        startDate: formatDate(a.startDate),
                        endDate: formatDate(a.endDate),
                        seasonName: season.name ?? '',
                        seasonId: a.seasonId,
                        departments: deptsForSeason.length > 0
                            ? deptsForSeason.join(', ')
                            : t('housing.report.noDepartments'),
                        isCrossSeason,
                    };
                })
                .sort((x, y) =>
                    x.bedIndex - y.bedIndex ||
                    x.startDate.localeCompare(y.startDate),
                );
            return { name: r.name, assignments: roomAssignments };
        });
        return { name: b.name, rooms };
    });
};

const createReport = async (seasonId: number) => {
    const workbook = reportCreateWorkbook();
    const worksheet = reportCreateWoksheet(workbook);

    reportAddTitleRow(worksheet, COLUMNS_NUMBER, t('housing.report.title'));

    const [buildingsRes, assignmentsRes] = await Promise.all([
        buildingApi.findAll(seasonId),
        roomApi.findRoomAssignments(String(seasonId), ''),
    ]);

    const buildings = buildingsRes.data;
    const assignments = (assignmentsRes.data ?? []) as any[];

    if (buildings.length === 0) {
        worksheet.insertRow(2, [t('housing.report.noBuildings')]);
        await addLogoImageToReport(worksheet, workbook);
        styleWorksheet(worksheet, 2);
        return workbook;
    }

    // Fetch rooms for every building (in parallel)
    const roomsResults = await Promise.all(
        buildings.map(async (b: any) => {
            const r = await roomApi.findAll(b.id, seasonId);
            return { buildingId: b.id, rooms: r.data as any[] };
        }),
    );
    const roomsByBuilding = new Map<number, Array<{ id: number; name: string }>>();
    for (const { buildingId, rooms } of roomsResults) {
        roomsByBuilding.set(
            buildingId,
            rooms.map((r) => ({ id: r.id, name: r.name }))
                .sort((a, b) => a.name.localeCompare(b.name)),
        );
    }

    // Fetch full employee records (for department info) for everyone in the assignments
    const employeeIds = Array.from(new Set(assignments.map((a) => a.employeeId))).map(String);
    const departmentsByEmployee = new Map<number, Map<number, string[]>>();
    if (employeeIds.length > 0) {
        try {
            const employeesRes = await employeesApi.findByIds(true, employeeIds);
            for (const emp of employeesRes.data as any[]) {
                const perSeason = new Map<number, string[]>();
                for (const etd of (emp.employeesToDepartments ?? [])) {
                    const sid = etd.seasonId;
                    const name = etd.department?.name;
                    if (!name || sid == null) continue;
                    const list = perSeason.get(sid) ?? [];
                    list.push(name);
                    perSeason.set(sid, list);
                }
                departmentsByEmployee.set(emp.id, perSeason);
            }
        } catch (err) {
            console.error('Failed to fetch employee departments for housing report:', err);
        }
    }

    const buildingGroups = buildBuildingGroups(
        buildings.map((b: any) => ({ id: b.id, name: b.name }))
            .sort((a, b) => a.name.localeCompare(b.name)),
        roomsByBuilding,
        assignments,
        seasonId,
        departmentsByEmployee,
    );

    addHeaderRow(worksheet);
    writeBody(worksheet, buildingGroups);

    // approximate row count for styling
    const totalRows = 2 + buildingGroups.reduce((acc, b) => {
        const roomsAcc = b.rooms.reduce((rAcc, r) => rAcc + Math.max(r.assignments.length, 1), 0);
        return acc + Math.max(roomsAcc, 1);
    }, 0);
    styleWorksheet(worksheet, totalRows);

    await addLogoImageToReport(worksheet, workbook);

    return workbook;
};

export const housingsReport = async (seasonId: number) => {
    const workbook = await createReport(seasonId);
    downloadReport(workbook);
};
